/**
 * Single source of truth for ERP navigation — primary slots, overflow categories, deep leaves.
 * Consolidates all capabilities into 9 authoritative parent modules + Home.
 * Primary chrome (rail / bottom bar) derives from here; drawer + launcher use overflow + leaves.
 */
import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  Hammer,
  HelpCircle,
  Home,
  MessageCircle,
  MoreHorizontal,
  Package,
  Scale,
  Settings2,
  ShoppingCart,
  UserRound,
  Wallet,
  Wrench,
} from "lucide-react";

export type NavCatalogSlot = {
  id: string;
  word: string;
  to: string;
  search?: Record<string, unknown>;
  icon: LucideIcon;
  match: (pathname: string) => boolean;
  moduleKey?: string;
};

export type NavOverflowCategory = {
  id: string;
  label: string;
  items: { id: string; label: string; to: string; search?: Record<string, string>; icon: LucideIcon }[];
};

export const PRIMARY_SLOT_IDS = [
  "home",
  "manufacturing",
  "stock",
  "sales",
  "people",
  "accounts",
  "payroll",
  "catalog",
  "reports",
  "settings",
] as const;

export const MOBILE_BOTTOM_SLOT_IDS = [
  "home",
  "sales",
  "stock",
  "manufacturing",
  "more",
] as const;

export const DESKTOP_RAIL_SLOT_IDS = PRIMARY_SLOT_IDS;

const MATCH = {
  home: (p: string) => p === "/app" || p === "/" || p === "/dashboard",
  manufacturing: (p: string) =>
    p.startsWith("/workshop") ||
    p.startsWith("/melt") ||
    p.startsWith("/settlement") ||
    p.startsWith("/manufacturing") ||
    p.startsWith("/refinery") ||
    p.startsWith("/mtg"),
  stock: (p: string) => p.startsWith("/stock") || p.startsWith("/barcode"),
  sales: (p: string) =>
    p.startsWith("/billing") ||
    p.startsWith("/orders") ||
    p.startsWith("/repair") ||
    p.startsWith("/scheme"),
  people: (p: string) =>
    p.startsWith("/people") ||
    p.startsWith("/crm"),
  accounts: (p: string) =>
    p.startsWith("/ledger") ||
    p.startsWith("/treasury") ||
    p.startsWith("/expenses") ||
    p.startsWith("/conversion") ||
    p.startsWith("/control/accounts") ||
    p.startsWith("/utilities"),
  payroll: (p: string) =>
    p.startsWith("/attendance"),
  catalog: (p: string) =>
    p.startsWith("/catalog") ||
    p.startsWith("/ui/catalog"),
  reports: (p: string) =>
    p.startsWith("/reports"),
  settings: (p: string) =>
    p.startsWith("/settings") ||
    p.startsWith("/control") ||
    p.startsWith("/hardware") ||
    p.startsWith("/communications") ||
    p.startsWith("/whatsapp") ||
    p.startsWith("/branches") ||
    p.startsWith("/ai-center") ||
    p.startsWith("/admin") ||
    p.startsWith("/customization") ||
    p.startsWith("/help"),
  more: (p: string) =>
    p === "/mobile/more" ||
    p.startsWith("/mobile/more"),
  // Backward compatibility aliases
  sell: (p: string) => MATCH.sales(p),
  customers: (p: string) => MATCH.people(p),
  make: (p: string) => MATCH.manufacturing(p),
  money: (p: string) => MATCH.accounts(p),
  admin: (p: string) => MATCH.settings(p) || p.startsWith("/admin"),
};

export const NAV_CATALOG_SLOTS: Record<string, NavCatalogSlot> = {
  home: { id: "home", word: "Home", to: "/app", icon: Home, match: MATCH.home },
  manufacturing: {
    id: "manufacturing",
    word: "Manufacturing",
    to: "/workshop",
    icon: Hammer,
    match: MATCH.manufacturing,
    moduleKey: "manufacturing",
  },
  stock: { id: "stock", word: "Stock", to: "/stock", icon: Package, match: MATCH.stock, moduleKey: "inventory" },
  sales: { id: "sales", word: "Sales", to: "/billing", icon: ShoppingCart, match: MATCH.sales, moduleKey: "billing" },
  people: {
    id: "people",
    word: "People / CRM",
    to: "/people",
    search: { tab: "customers" },
    icon: UserRound,
    match: MATCH.people,
    moduleKey: "crm_communications",
  },
  accounts: { id: "accounts", word: "Accounts", to: "/ledger", icon: Wallet, match: MATCH.accounts },
  payroll: { id: "payroll", word: "Payroll", to: "/attendance", icon: Banknote, match: MATCH.payroll },
  catalog: { id: "catalog", word: "Catalog", to: "/catalog", icon: BookOpen, match: MATCH.catalog },
  reports: { id: "reports", word: "Reports", to: "/reports", icon: BarChart3, match: MATCH.reports, moduleKey: "reports" },
  settings: { id: "settings", word: "Settings", to: "/settings", icon: Settings2, match: MATCH.settings },
  more: { id: "more", word: "More", to: "/mobile/more", icon: MoreHorizontal, match: MATCH.more },

  // Backward compatibility slot aliases
  sell: { id: "sell", word: "Sales", to: "/billing", icon: ShoppingCart, match: MATCH.sales, moduleKey: "billing" },
  customers: {
    id: "customers",
    word: "People",
    to: "/people",
    search: { tab: "customers" },
    icon: UserRound,
    match: MATCH.people,
    moduleKey: "crm_communications",
  },
  make: { id: "make", word: "Manufacturing", to: "/workshop", icon: Hammer, match: MATCH.manufacturing, moduleKey: "manufacturing" },
  money: { id: "money", word: "Accounts", to: "/ledger", icon: Wallet, match: MATCH.accounts },
  admin: { id: "admin", word: "Settings", to: "/settings", icon: Settings2, match: MATCH.settings },
};

export const OVERFLOW_CATEGORIES: NavOverflowCategory[] = [
  {
    id: "manufacturing",
    label: "Manufacturing Flow",
    items: [
      { id: "sauda", label: "Bullion / Sauda", to: "/utilities/sauda-book", icon: Wallet },
      { id: "dhadi", label: "Jobs / Dhadi", to: "/workshop/dhadi-groups", icon: Hammer },
      { id: "nave", label: "Nave Issue", to: "/workshop/gold-book", icon: Hammer },
      { id: "custody", label: "Karigar Custody", to: "/workshop/bench-custody", icon: UserRound },
      { id: "jama", label: "Jama Return", to: "/workshop/jama", icon: Package },
      { id: "recovery", label: "Recovery / Vibrator", to: "/workshop/vibrator", icon: Wrench },
      { id: "reconciliation", label: "Reconciliation", to: "/workshop/reconciliation", icon: Scale },
      { id: "settlement", label: "Settlement", to: "/settlement/new", icon: Wallet },
      { id: "qc_huid", label: "QC / HUID", to: "/stock/hallmark", icon: Package },
    ],
  },
  {
    id: "stock",
    label: "Stock & Inventory",
    items: [
      { id: "ready", label: "Ready Stock", to: "/stock", search: { tab: "ready" }, icon: Package },
      { id: "barcode", label: "Barcode & Tagging", to: "/stock/entry", icon: Package },
      { id: "lots", label: "Raw Lots / Bullion", to: "/stock/lots", icon: Package },
      { id: "transfers", label: "Stock Transfers", to: "/stock/transfers", icon: Package },
      { id: "boxes", label: "Showcase Trays", to: "/stock/boxes", icon: Package },
      { id: "audit", label: "Physical Audit", to: "/stock/verification", icon: Package },
    ],
  },
  {
    id: "sales",
    label: "Showroom Sales",
    items: [
      { id: "pos", label: "Retail POS Billing", to: "/billing/new", icon: ShoppingCart },
      { id: "invoices", label: "Sales Invoices", to: "/billing", icon: ShoppingCart },
      { id: "wholesale", label: "Wholesale Billing", to: "/billing/wholesale", icon: ShoppingCart },
      { id: "estimates", label: "Quotations & Estimates", to: "/billing/estimates", icon: ShoppingCart },
      { id: "orders", label: "Customer Orders", to: "/orders", icon: ShoppingCart },
      { id: "urd", label: "Old Gold / URD Exchange", to: "/utilities/urd-purchase", icon: Wallet },
    ],
  },
  {
    id: "people_crm",
    label: "People & CRM",
    items: [
      { id: "customers", label: "Customers (Grahak)", to: "/people", search: { tab: "customers" }, icon: UserRound },
      { id: "suppliers", label: "Suppliers", to: "/people", search: { tab: "suppliers" }, icon: UserRound },
      { id: "karigars", label: "Karigars (Artisans)", to: "/workshop/worker-books", icon: UserRound },
      { id: "employees", label: "Staff & Employees", to: "/attendance", search: { tab: "employees" }, icon: UserRound },
      { id: "crm", label: "CRM & Leads", to: "/crm/leads", icon: UserRound },
      { id: "communications", label: "Communications", to: "/communications", icon: MessageCircle },
    ],
  },
  {
    id: "accounts",
    label: "Accounts & Ledgers",
    items: [
      { id: "vault", label: "Gold Vault @995", to: "/ledger", icon: Wallet },
      { id: "daybook", label: "Daybook (Rojmel)", to: "/treasury/cash-book", icon: Wallet },
      { id: "vouchers", label: "Payment & Receipt Vouchers", to: "/treasury/vouchers", icon: Wallet },
      { id: "bank", label: "Bank Reconciliation", to: "/treasury/bank-reconciliation", icon: Building2 },
      { id: "gst", label: "GST Returns & Registers", to: "/reports/gst-returns", icon: BarChart3 },
    ],
  },
  {
    id: "payroll",
    label: "Workforce & Payroll",
    items: [
      { id: "attendance", label: "Attendance Register", to: "/attendance", icon: UserRound },
      { id: "salary", label: "Monthly Payroll", to: "/attendance", search: { tab: "payroll" }, icon: Banknote },
      { id: "advances", label: "Advances & Loans", to: "/attendance", search: { tab: "advances" }, icon: Wallet },
      { id: "passbook", label: "Worker Passbook", to: "/attendance", search: { tab: "passbook" }, icon: Banknote },
    ],
  },
  {
    id: "catalog",
    label: "Digital Catalog",
    items: [
      { id: "items", label: "Products & Items", to: "/catalog", icon: BookOpen },
      { id: "designs", label: "Design Library", to: "/catalog/designs", icon: BookOpen },
      { id: "media", label: "Images & Showcase", to: "/ui/catalog", icon: BookOpen },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    items: [
      { id: "reports-hub", label: "Reports Cockpit", to: "/reports", icon: BarChart3 },
      { id: "sales-reg", label: "Sales Register", to: "/reports/sales-register", icon: BarChart3 },
      { id: "fine-rojmel", label: "Fine Rojmel", to: "/reports/fine-rojmel", icon: BarChart3 },
      { id: "daily-close", label: "Daily Close (Day Done)", to: "/reports/daily-close", icon: BarChart3 },
      { id: "metal-pos", label: "Metal Position (@995)", to: "/reports/metal-position", icon: BarChart3 },
      { id: "wastage", label: "Karigar Wastage Audit", to: "/reports/worker", icon: BarChart3 },
    ],
  },
  {
    id: "settings",
    label: "System Settings",
    items: [
      { id: "settings-hub", label: "Firm & Branches", to: "/settings", icon: Settings2 },
      { id: "rates", label: "Live Bullion Rates", to: "/control/rates", icon: Wallet },
      { id: "printing", label: "Printing Profiles", to: "/control/customization", icon: Wrench },
      { id: "automation", label: "Automation Engine", to: "/settings/automation", icon: Bot },
      { id: "whatsapp", label: "WhatsApp Setup", to: "/settings/whatsapp", icon: MessageCircle },
      { id: "security", label: "Security Center", to: "/settings/security-center", icon: Settings2 },
    ],
  },
  {
    id: "help",
    label: "Help",
    items: [{ id: "help", label: "Help & Support", to: "/help", icon: HelpCircle }],
  },
];

export function getCatalogSlot(id: string): NavCatalogSlot {
  const slot = NAV_CATALOG_SLOTS[id];
  if (!slot) throw new Error(`Unknown nav catalog slot: ${id}`);
  return slot;
}

export function getMobileBottomSlots(): NavCatalogSlot[] {
  return MOBILE_BOTTOM_SLOT_IDS.map(getCatalogSlot);
}

export function getDesktopRailSlots(): NavCatalogSlot[] {
  return DESKTOP_RAIL_SLOT_IDS.map(getCatalogSlot);
}

export function isCatalogPrimaryPath(pathname: string): boolean {
  if (!pathname) return false;
  if (pathname.startsWith("/platform") || pathname.startsWith("/saas")) return false;
  return Object.values(NAV_CATALOG_SLOTS).some((s) => s.match(pathname)) || pathname === "/app";
}

/** Firm Admin hub sections — single source for /admin cards. */
export type AdminHubLink = {
  to: string;
  label: string;
  note: string;
  iconName: string;
};

export type AdminHubSection = {
  id: string;
  title: string;
  description: string;
  items: AdminHubLink[];
};

export const ADMIN_HUB_SECTIONS: AdminHubSection[] = [
  {
    id: "people",
    title: "People & access",
    description: "Users, roles, security, and attendance.",
    items: [
      { to: "/settings", label: "Users & roles", iconName: "Users", note: "Staff accounts and permissions" },
      { to: "/settings/security-center", label: "Security center", iconName: "Shield", note: "Sessions, devices, audit" },
      { to: "/attendance", label: "Attendance & payroll", iconName: "Users", note: "Team time and wages" },
    ],
  },
  {
    id: "business",
    title: "Business & billing",
    description: "Firm profile, gold rates, credits, and platform subscription.",
    items: [
      { to: "/settings", label: "Business defaults", iconName: "Building2", note: "Gold rates and firm profile — Business tab" },
      { to: "/settings", label: "Credits wallet", iconName: "Coins", note: "WhatsApp & AI credits — Credits tab" },
      { to: "/settings/license", label: "License & plans", iconName: "CreditCard", note: "Subscription and AMC invoices" },
      { to: "/control/rates", label: "Live bullion rates", iconName: "Coins", note: "24K / 22K rate feeds" },
    ],
  },
  {
    id: "communications",
    title: "Communications",
    description: "Email, WhatsApp, and customer messaging.",
    items: [
      { to: "/settings/integrations-hub", label: "Integrations hub", iconName: "MessageSquare", note: "API keys, WA, SMS, email" },
      { to: "/settings/whatsapp", label: "WhatsApp automation", iconName: "MessageSquare", note: "Transactional notices" },
      { to: "/settings/communications", label: "Email automation", iconName: "Mail", note: "SMTP and auto-send rules" },
    ],
  },
  {
    id: "modules",
    title: "Modules & automation",
    description: "Enable modules, automations, workflow, and hardware.",
    items: [
      { to: "/settings", label: "Module toggles", iconName: "ToggleLeft", note: "Enable ERP modules — Modules tab" },
      { to: "/control/customization", label: "Customization", iconName: "Settings2", note: "Fields, labels, print layouts" },
      { to: "/hardware", label: "Hardware & devices", iconName: "Wrench", note: "Printers, scales, scanners" },
      { to: "/settings/automation", label: "Automation blocks", iconName: "Bot", note: "Gold-industry no-code automations" },
      { to: "/settings/workflow", label: "Workflow engine", iconName: "Settings2", note: "Process books and manufacturing steps" },
    ],
  },
  {
    id: "firm",
    title: "Firm setup",
    description: "Branches, extensions, and firm-wide configuration.",
    items: [
      { to: "/branches", label: "Branches", iconName: "Building2", note: "Multi-branch identity" },
      { to: "/settings", label: "Extensions store", iconName: "Package", note: "Add-on modules — Extensions tab" },
      { to: "/settings/integrations-hub", label: "API keys & developer", iconName: "Shield", note: "MCP keys and webhooks" },
      { to: "/settings", label: "All settings", iconName: "Settings2", note: "Full settings hub" },
    ],
  },
];

export const ADMIN_TOOL_LINKS: AdminHubLink[] = [
  { to: "/barcode", label: "Barcode", iconName: "Package", note: "" },
  { to: "/utilities", label: "Utilities", iconName: "Wrench", note: "" },
  { to: "/ai-center", label: "Clearmove AI", iconName: "Bot", note: "" },
  { to: "/reports", label: "Reports", iconName: "BarChart3", note: "" },
  { to: "/help", label: "Help & support", iconName: "HelpCircle", note: "" },
];
